import { useMemo, useSyncExternalStore } from 'react';
import type { AnyEntity, EntityOf, Kind } from './types';

// The decrypted working set, in memory. Screens read from here synchronously, so nothing
// ever waits on IndexedDB or the network. Writes go through core/repo.

type Listener = () => void;

class MemoryStore {
  private maps = new Map<Kind, Map<string, AnyEntity>>();
  private versions = new Map<Kind, number>();
  private listeners = new Map<Kind, Set<Listener>>();
  private anyListeners = new Set<Listener>();
  private listCache = new Map<Kind, { v: number; list: AnyEntity[] }>();
  private allCache = new Map<Kind, { v: number; list: AnyEntity[] }>();
  private groupCache = new Map<string, { v: number; groups: Map<unknown, AnyEntity[]> }>();
  private kindOfId = new Map<string, Kind>();
  private batchDepth = 0;
  private pending = new Set<Kind>();
  /** kinds fully loaded from disk after unlock */
  private loaded = new Set<Kind>();

  private map(kind: Kind): Map<string, AnyEntity> {
    let m = this.maps.get(kind);
    if (!m) {
      m = new Map();
      this.maps.set(kind, m);
    }
    return m;
  }

  version(kind: Kind): number {
    return this.versions.get(kind) ?? 0;
  }

  get<K extends Kind>(kind: K, id: string | null | undefined): EntityOf<K> | undefined {
    if (!id) return undefined;
    return this.maps.get(kind)?.get(id) as EntityOf<K> | undefined;
  }

  /** Look up any entity by id when the kind isn't known. */
  find(id: string): AnyEntity | undefined {
    const kind = this.kindOfId.get(id);
    return kind ? this.maps.get(kind)?.get(id) : undefined;
  }

  /** Live (not deleted) entities of a kind. Same array instance until the kind changes. */
  list<K extends Kind>(kind: K): EntityOf<K>[] {
    const v = this.version(kind);
    const hit = this.listCache.get(kind);
    if (hit && hit.v === v) return hit.list as EntityOf<K>[];
    const list: AnyEntity[] = [];
    for (const e of this.maps.get(kind)?.values() ?? []) if (!e.deleted) list.push(e);
    this.listCache.set(kind, { v, list });
    return list as EntityOf<K>[];
  }

  /** Everything of a kind including deleted (trash). */
  listAll<K extends Kind>(kind: K): EntityOf<K>[] {
    const v = this.version(kind);
    const hit = this.allCache.get(kind);
    if (hit && hit.v === v) return hit.list as EntityOf<K>[];
    const list = [...(this.maps.get(kind)?.values() ?? [])];
    this.allCache.set(kind, { v, list });
    return list as EntityOf<K>[];
  }

  /** Live entities grouped by a field (e.g. tasks by listId). Cached per version. */
  group<K extends Kind, F extends keyof EntityOf<K>>(
    kind: K,
    field: F,
  ): Map<EntityOf<K>[F], EntityOf<K>[]> {
    const key = `${kind}:${String(field)}`;
    const v = this.version(kind);
    const hit = this.groupCache.get(key);
    if (hit && hit.v === v) return hit.groups as Map<EntityOf<K>[F], EntityOf<K>[]>;
    const groups = new Map<unknown, AnyEntity[]>();
    for (const e of this.list(kind)) {
      const g = (e as unknown as Record<string, unknown>)[field as string];
      let arr = groups.get(g);
      if (!arr) {
        arr = [];
        groups.set(g, arr);
      }
      arr.push(e);
    }
    this.groupCache.set(key, { v, groups });
    return groups as Map<EntityOf<K>[F], EntityOf<K>[]>;
  }

  put(entity: AnyEntity) {
    this.map(entity.kind).set(entity.id, entity);
    this.kindOfId.set(entity.id, entity.kind);
    this.changed(entity.kind);
  }

  putMany(entities: AnyEntity[]) {
    this.batch(() => {
      for (const e of entities) this.put(e);
    });
  }

  /** Drop from memory only (vault locked, local purge). */
  evict(kind: Kind, id: string) {
    if (this.maps.get(kind)?.delete(id)) {
      this.kindOfId.delete(id);
      this.changed(kind);
    }
  }

  evictWhere(pred: (e: AnyEntity) => boolean) {
    this.batch(() => {
      for (const [kind, m] of this.maps) {
        for (const [id, e] of m) {
          if (pred(e)) {
            m.delete(id);
            this.kindOfId.delete(id);
            this.changed(kind);
          }
        }
      }
    });
  }

  clear() {
    const kinds = [...this.maps.keys()];
    this.maps.clear();
    this.kindOfId.clear();
    this.loaded.clear();
    this.batch(() => {
      for (const k of kinds) this.changed(k);
    });
  }

  markLoaded(kinds: Iterable<Kind>) {
    for (const k of kinds) {
      this.loaded.add(k);
      this.changed(k);
    }
  }

  isLoaded(kind: Kind): boolean {
    return this.loaded.has(kind);
  }

  batch(fn: () => void) {
    this.batchDepth++;
    try {
      fn();
    } finally {
      this.batchDepth--;
      if (this.batchDepth === 0 && this.pending.size) {
        const kinds = [...this.pending];
        this.pending.clear();
        for (const k of kinds) this.emit(k);
      }
    }
  }

  subscribe(kind: Kind, l: Listener): () => void {
    let set = this.listeners.get(kind);
    if (!set) {
      set = new Set();
      this.listeners.set(kind, set);
    }
    set.add(l);
    return () => set.delete(l);
  }

  subscribeAny(l: Listener): () => void {
    this.anyListeners.add(l);
    return () => this.anyListeners.delete(l);
  }

  private changed(kind: Kind) {
    this.versions.set(kind, this.version(kind) + 1);
    if (this.batchDepth > 0) this.pending.add(kind);
    else this.emit(kind);
  }

  private emit(kind: Kind) {
    for (const l of this.listeners.get(kind) ?? []) l();
    for (const l of this.anyListeners) l();
  }
}

export const store = new MemoryStore();

// ------------------------------------------------------------------ hooks

/** Live entities of a kind; re-renders when that kind changes. */
export function useList<K extends Kind>(kind: K): EntityOf<K>[] {
  return useSyncExternalStore(
    (cb) => store.subscribe(kind, cb),
    () => store.list(kind),
  );
}

/** Including deleted ones (for Trash). */
export function useListAll<K extends Kind>(kind: K): EntityOf<K>[] {
  return useSyncExternalStore(
    (cb) => store.subscribe(kind, cb),
    () => store.listAll(kind),
  );
}

export function useEntity<K extends Kind>(
  kind: K,
  id: string | null | undefined,
): EntityOf<K> | undefined {
  return useSyncExternalStore(
    (cb) => store.subscribe(kind, cb),
    () => store.get(kind, id),
  );
}

/** Entities grouped by a field, e.g. useGroup('task', 'listId').get(listId). */
export function useGroup<K extends Kind, F extends keyof EntityOf<K>>(
  kind: K,
  field: F,
): Map<EntityOf<K>[F], EntityOf<K>[]> {
  return useSyncExternalStore(
    (cb) => store.subscribe(kind, cb),
    () => store.group(kind, field),
  );
}

/** Derived data from one kind, recomputed only when that kind changes. */
export function useDerived<K extends Kind, R>(
  kind: K,
  fn: (items: EntityOf<K>[]) => R,
  deps: unknown[] = [],
): R {
  const items = useList(kind);
  // biome-ignore lint/correctness/useExhaustiveDependencies: caller controls deps
  return useMemo(() => fn(items), [items, ...deps]);
}

export function useKindLoaded(kind: Kind): boolean {
  return useSyncExternalStore(
    (cb) => store.subscribe(kind, cb),
    () => store.isLoaded(kind),
  );
}
